import { AccordionItem, Button, Tooltip } from '@mm/components';
import { ShopifyIcon } from '@mm/icons';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { PromoterTypes } from '@mm/state/model/PromoterModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ConfigProvider, Switch, Table } from 'antd';
import classNames from 'classnames';
import { match } from 'path-to-regexp';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import {
  useCustomPromoterJSCodes,
  usePromoterJSCode,
} from '../../../../startBotPromoters/common/use-promoter-js-code';
import styles from './ShopifySignal.module.css';

export const ShopifySignal = ({
  data,
  isShopifySignalActive,
  handleShopifySignalToggle,
}: {
  data: {
    productView?: number;
    collectionView?: number;
    checkoutStarted?: number;
    orderedProduct?: number;
    addedPaymentInfo?: number;
    addedToCart?: number;
    returnVisit?: number;
    placedOrder?: number;
    viewContent?: number;
    pageView?: number;
    submittedSearch?: number;
  };
  isShopifySignalActive: boolean;
  handleShopifySignalToggle: (
    checked: boolean,
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.KeyboardEvent<HTMLButtonElement>,
  ) => void;
}) => {
  const eventsToNamesMap = {
    pageView: 'Page view',
    search: 'Search',
    productView: 'Viewed product',
    addToCart: 'Add to cart',
    addedPaymentInfo: 'Add payment info',
    collectionView: 'Viewed collection',
    checkoutStarted: 'Begin checkout',
    orderedProduct: 'Ordered product',
    addedToCart: 'Added to cart',
    returnVisit: 'Return visit',
    placedOrder: 'Placed order',
    viewContent: 'View content',
    submittedSearch: 'Submitted search',
  };

  const dataSource = Object.entries(data).map(([key, value]) => ({
    key,
    name: eventsToNamesMap[key],
    value,
  }));

  const columns = [
    {
      title: 'Events',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Events sent',
      dataIndex: 'value',
      key: 'value',
    },
  ];

  const { showSlideoutMenu } = useViewActions();

  const matchRoute = match(`${BotBuilderRoutes.integrations.path}/:route`)(
    location.pathname,
  ) as {
    params?: { chatbotId?: string };
  };
  const chatbotId = useSelector(activeChatbotIdSelector);
  const matchId = parseInt(matchRoute?.params?.chatbotId || '', 10);

  const { retrieveAll } = usePromoterCRUDActions();

  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const botId = !isNaN(matchId)
    ? matchId
    : activeFacebookPage?.activeBotId || chatbotId;

  const [promoterId, setPromoterId] = useState<number | undefined>();

  const [promotersReady, setPromotersReady] = useState(false);

  const retrieveGetemailPromoters = async () => {
    try {
      if (botId) {
        const res = await retrieveAll(botId, 'getemails');
        if (res.length) {
          const firstPromoter = res?.[0];
          setPromoterId(firstPromoter.id);
        }
      }
    } finally {
      setPromotersReady(true);
    }
  };

  useEffect(() => {
    retrieveGetemailPromoters();
  }, []);

  const promoter = useSelector((state: StoreState) =>
    state.promoter.entities.find(
      promoter => promoter.id?.toString() === promoterId?.toString(),
    ),
  );

  const promoterJSCode = usePromoterJSCode(promoter);
  const promoterJSCode2Map = useCustomPromoterJSCodes(promoter);

  const openShopifyInstallationGuide = () => {
    showSlideoutMenu('customerChatInstallationGuide', {
      siteType: 'Shopify',
      promoterJSCode,
      promoterJSCode2: promoterJSCode2Map.Shopify,
      promoterType: PromoterTypes.GETEMAILS,
      onCopyClick: () => {},
    });
  };

  return (
    <AccordionItem
      className={classNames('integrations-item', styles.container)}
      closeOnOutsideClick={false}
      openOnMount={false}
      showArrow={true}
      caretStyleIcon
      icon={
        <Tooltip
          title="When you activate Shopify Signal, Manual Signal will be stopped
          automatically if it has been setup. Only one can work at time."
          placement="bottom"
        >
          <div className={styles.switchContainer}>
            <Switch
              checked={isShopifySignalActive}
              onChange={handleShopifySignalToggle}
            />
          </div>
        </Tooltip>
      }
      titleComponent={
        <div className={styles.titleContainer}>
          <div className={styles.shopifyIcon}>
            <ShopifyIcon />
          </div>
          <div className={styles.titleInfo}>
            <div>
              <span className={styles.title}>Shopify Signal</span>
              <span className={styles.titleGreen}>Recommended</span>
            </div>
            <div>Identify high value interaction on your website</div>
          </div>
        </div>
      }
    >
      <ConfigProvider
        theme={{
          components: {
            Table: {
              headerBg: 'white',
              borderColor: '#D9D9D9',
            },
          },
        }}
      >
        <Table
          dataSource={dataSource}
          columns={columns}
          bordered
          pagination={false}
          size="small"
        />
      </ConfigProvider>

      <div className={styles.greyBlock}>
        <div className={styles.codeHeaderContainer}>
          <div className={styles.codeHeaderText}>
            <div className={styles.codeTitleContainer}>
              Install both codes on Shopify
            </div>
            <div className={styles.codeSubtitle}>
              Identify high value interaction on your website
            </div>
          </div>

          <div className={styles.codeButtonsBlock}>
            <Button
              css={{ container: styles.codeBlockCopyButton }}
              onClick={openShopifyInstallationGuide}
              disabled={!promotersReady}
            >
              See Installation Guide
            </Button>
          </div>
        </div>
      </div>
    </AccordionItem>
  );
};
